import React, { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  doc,
  where
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { useLanguage } from '../i18n';

const styleTag = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
  .checkin-tab-btn {
    transition: color 0.2s !important;
  }
  .checkin-tab-btn:hover {
    color: #008081 !important;
  }
  .checkin-option-btn {
    transition: all 0.15s !important;
  }
  .checkin-option-btn:hover {
    border-color: #008081 !important;
    background-color: rgba(0,128,129,0.06) !important;
  }
  .checkin-submit-btn {
    transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s !important;
  }
  .checkin-submit-btn:hover:not(:disabled) {
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 20px rgba(0,128,129,0.4) !important;
  }
  .checkin-type-btn {
    transition: all 0.2s !important;
  }
  .checkin-type-btn:hover {
    border-color: #008081 !important;
    background-color: rgba(0,128,129,0.06) !important;
    transform: translateY(-1px) !important;
  }
  .checkin-textarea:focus {
    border-color: #008081 !important;
    box-shadow: 0 0 0 3px rgba(0,128,129,0.15) !important;
    outline: none !important;
  }
  .checkin-history-card {
    transition: border-color 0.2s !important;
  }
  .checkin-history-card:hover {
    border-color: rgba(0,128,129,0.3) !important;
  }
`;

export default function CheckinView({ user, userData, partner, activeVeylId, theme, t: themeTokens, translate: t }) {
  const dark = theme === 'dark';

  const colors = {
    cardBg:         dark ? 'rgba(255,255,255,0.02)' : '#FFFFFF',
    cardBorder:     dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    text:           dark ? '#ffffff' : '#1B2D44',
    subtext:        dark ? 'rgba(255,255,255,0.45)' : '#6b7280',
    mutedText:      dark ? 'rgba(255,255,255,0.4)' : '#9ca3af',
    veryMuted:      dark ? 'rgba(255,255,255,0.3)' : '#d1d5db',
    tabBorder:      dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    tabInactive:    dark ? 'rgba(255,255,255,0.4)' : '#6b7280',
    optionBg:       dark ? 'rgba(255,255,255,0.03)' : '#f9fafb',
    optionBorder:   dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
    optionText:     dark ? 'rgba(255,255,255,0.8)' : '#374151',
    optionDesc:     dark ? 'rgba(255,255,255,0.4)' : '#9ca3af',
    optionDotOff:   dark ? 'rgba(255,255,255,0.2)' : '#d1d5db',
    inputBg:        dark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    inputBorder:    dark ? 'rgba(255,255,255,0.1)' : '#d1d5db',
    inputColor:     dark ? '#ffffff' : '#1B2D44',
    historyCardBg:  dark ? 'rgba(255,255,255,0.03)' : '#f9fafb',
    historyCardBorder: dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    dateBadgeBg:    dark ? 'rgba(255,255,255,0.05)' : '#f3f4f6',
    dateBadgeText:  dark ? 'rgba(255,255,255,0.3)' : '#9ca3af',
    statusText:     dark ? 'rgba(255,255,255,0.65)' : '#4b5563',
    myInsightBg:    dark ? 'rgba(0,128,129,0.08)' : 'rgba(0,128,129,0.06)',
    myInsightBorder:dark ? 'rgba(0,128,129,0.15)' : 'rgba(0,128,129,0.2)',
    myInsightText:  dark ? 'rgba(255,255,255,0.7)' : '#374151',
    pInsightBg:     dark ? 'rgba(75,44,94,0.15)' : 'rgba(75,44,94,0.06)',
    pInsightBorder: dark ? 'rgba(192,132,252,0.15)' : 'rgba(192,132,252,0.2)',
    pInsightText:   dark ? 'rgba(255,255,255,0.7)' : '#374151',
    emptyText:      dark ? 'rgba(255,255,255,0.3)' : '#9ca3af',
    backBtn:        dark ? 'rgba(255,255,255,0.4)' : '#6b7280',
    labelColor:     dark ? 'rgba(255,255,255,0.4)' : '#6b7280',
    arrowColor:     dark ? 'rgba(255,255,255,0.25)' : '#d1d5db',
    lockText:       dark ? 'rgba(255,255,255,0.25)' : '#9ca3af',
  };

  const [activeTab, setActiveTab] = useState('new');
  const [checkInType, setCheckInType] = useState(null);
  const [dailyStatus, setDailyStatus] = useState('');
  const [dailyNote, setDailyNote] = useState('');
  const [weeklyStatus, setWeeklyStatus] = useState('');
  const [weeklyLearning, setWeeklyLearning] = useState('');
  const [weeklyPattern, setWeeklyPattern] = useState('');
  const [weeklyChange, setWeeklyChange] = useState('');
  const [goalAchieved, setGoalAchieved] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [myCheckIns, setMyCheckIns] = useState([]);
  const [partnerCheckIns, setPartnerCheckIns] = useState([]);

  const veylStatus = userData?.activeVeyl?.status || userData?.status;
  const alreadyAchieved = userData?.activeVeyl?.goalAchieved || userData?.goalAchieved;
  const isComplete = veylStatus === 'complete';
  const partnerId = userData?.activeVeyl?.partnerId || userData?.partnerId;

  const card = {
    background: colors.cardBg,
    backdropFilter: 'blur(10px)',
    borderRadius: '1.25rem',
    padding: '2rem',
    border: `1px solid ${colors.cardBorder}`,
    animation: 'fadeInUp 0.4s ease'
  };

  const optionBtn = (selected) => ({
    padding: '1rem 1.25rem',
    border: selected ? '2px solid #008081' : `2px solid ${colors.optionBorder}`,
    backgroundColor: selected ? 'rgba(0,128,129,0.12)' : colors.optionBg,
    borderRadius: '0.875rem',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all 0.15s'
  });

  const textarea = {
    width: '100%',
    padding: '0.875rem 1rem',
    border: `1px solid ${colors.inputBorder}`,
    borderRadius: '0.75rem',
    height: '6rem',
    fontFamily: 'inherit',
    fontSize: '0.95rem',
    backgroundColor: colors.inputBg,
    color: colors.inputColor,
    resize: 'vertical',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, box-shadow 0.2s'
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'yes':        return t('yesLabel') + ' ' + t('madeProgress');
      case 'some':       return t('aLittle') + ' ' + t('didSomething');
      case 'no':         return t('noLabel') + ' ' + t('didntWork');
      case 'did':        return t('metCommitment');
      case 'partial':    return t('madePartial');
      case 'didnt':      return t('didntMake');
      case 'rethinking': return t('needToAdjust');
      default:           return status;
    }
  };

  useEffect(() => {
    if (!user || !activeVeylId) return;
    const q = query(
      collection(db, 'checkins'),
      where('userId', '==', user.uid),
      where('veylId', '==', activeVeylId),
      orderBy('timestamp', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = [];
      snapshot.forEach(d => items.push({ id: d.id, ...d.data() }));
      setMyCheckIns(items);
    });
    return () => unsubscribe();
  }, [user, activeVeylId]);

  useEffect(() => {
    if (!partnerId) return;
    const q = query(
      collection(db, 'checkins'),
      where('userId', '==', partnerId),
      orderBy('timestamp', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = [];
      snapshot.forEach(d => items.push({ id: d.id, ...d.data() }));
      setPartnerCheckIns(items);
    });
    return () => unsubscribe();
  }, [partnerId]);

  const submitDailyCheckIn = async () => {
    if (!dailyStatus) { alert(t('didYouWork')); return; }
    setLoading(true);
    const functions = getFunctions();
    const sendNotification = httpsCallable(functions, 'sendNotification');
    try {
      await addDoc(collection(db, 'checkins'), {
        userId: user.uid, veylId: activeVeylId, partnerId,
        type: 'daily', status: dailyStatus, note: dailyNote,
        goalAchieved: goalAchieved === 'yes', timestamp: serverTimestamp()
      });
      if (goalAchieved === 'yes' && activeVeylId && !alreadyAchieved) {
        await updateDoc(doc(db, 'veyls', activeVeylId), { goalAchieved: true });
        if (partnerId) {
          await sendNotification({
            recipientId: partnerId,
            type: 'goal_achieved',
            message: `${userData.name} says they've achieved their goal! 🎉`,
          }).catch(err => console.error('Goal achieved notification error:', err));
        }
      }
      if (dailyStatus === 'no' && partnerId) {
        await sendNotification({
          recipientId: partnerId,
          type: 'support',
          message: `${userData.name} is struggling today. Send them some support!`,
        }).catch(err => console.error('Support notification error:', err));
      }
      setSubmitted(true);
      setDailyStatus(''); setDailyNote(''); setGoalAchieved('');
    } catch (err) { alert(t('error') + ': ' + err.message); }
    finally { setLoading(false); }
  };

  const submitWeeklyCheckIn = async () => {
    if (!weeklyStatus || !weeklyLearning || !weeklyPattern || !weeklyChange) {
      alert(t('submitting')); return;
    }
    setLoading(true);
    const functions = getFunctions();
    const sendNotification = httpsCallable(functions, 'sendNotification');
    try {
      await addDoc(collection(db, 'checkins'), {
        userId: user.uid, veylId: activeVeylId, partnerId,
        type: 'weekly', status: weeklyStatus,
        learning: weeklyLearning, pattern: weeklyPattern, change: weeklyChange,
        goalAchieved: goalAchieved === 'yes', timestamp: serverTimestamp()
      });
      if (goalAchieved === 'yes' && activeVeylId && !alreadyAchieved) {
        await updateDoc(doc(db, 'veyls', activeVeylId), { goalAchieved: true });
        if (partnerId) {
          await sendNotification({
            recipientId: partnerId,
            type: 'goal_achieved',
            message: `${userData.name} says they've achieved their goal! 🎉`,
          }).catch(err => console.error('Goal achieved notification error:', err));
        }
      }
      if ((weeklyStatus === 'didnt' || weeklyStatus === 'rethinking') && partnerId) {
        await sendNotification({
          recipientId: partnerId,
          type: 'support',
          message: `${userData.name} is struggling this week. Send them some support!`,
        }).catch(err => console.error('Support notification error:', err));
      }
      setSubmitted(true);
      setWeeklyStatus(''); setWeeklyLearning(''); setWeeklyPattern('');
      setWeeklyChange(''); setGoalAchieved('');
    } catch (err) { alert(t('error') + ': ' + err.message); }
    finally { setLoading(false); }
  };

  const GoalAchievedQuestion = () => (
    !alreadyAchieved && !isComplete ? (
      <div style={{
        marginBottom: '1.5rem', padding: '1.25rem',
        backgroundColor: 'rgba(75,44,94,0.15)',
        border: '1px solid rgba(192,132,252,0.2)',
        borderRadius: '0.875rem'
      }}>
        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.875rem', color: '#c084fc' }}>
          {t('goalAchievedQuestion')}
        </label>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {[
            { value: 'yes', label: t('yesDidIt') },
            { value: 'no',  label: t('notYetBtn') },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setGoalAchieved(opt.value)}
              style={{
                flex: 1, padding: '0.75rem',
                border: goalAchieved === opt.value ? '2px solid #c084fc' : `2px solid ${colors.optionBorder}`,
                backgroundColor: goalAchieved === opt.value ? 'rgba(192,132,252,0.15)' : colors.optionBg,
                borderRadius: '0.75rem', cursor: 'pointer',
                fontWeight: goalAchieved === opt.value ? '700' : '400',
                color: goalAchieved === opt.value ? '#c084fc' : colors.tabInactive,
                fontSize: '0.875rem', transition: 'all 0.15s'
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    ) : null
  );

  if (submitted) {
    return (
      <div style={{ maxWidth: '42rem', margin: '0 auto' }}>
        <style>{styleTag}</style>
        <div style={{ ...card, textAlign: 'center', padding: '3rem 2rem' }}>
          <div style={{
            width: '72px', height: '72px', margin: '0 auto 1.5rem',
            background: 'linear-gradient(135deg, #008081, #005f5f)',
            borderRadius: '50%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '2rem',
            boxShadow: '0 8px 24px rgba(0,128,129,0.35)'
          }}>✅</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '0.5rem', color: colors.text, letterSpacing: '-0.02em' }}>
            {t('checkinSubmitted')}
          </h2>
          <p style={{ color: colors.subtext, marginBottom: '1.5rem', lineHeight: 1.6 }}>
            {t('checkinSubmittedDesc').replace('{name}', partner?.name || t('yourPartner'))}
          </p>
          {(goalAchieved === 'yes' || alreadyAchieved) && (
            <div style={{
              marginBottom: '1.5rem', padding: '1rem',
              backgroundColor: 'rgba(192,132,252,0.1)',
              border: '1px solid rgba(192,132,252,0.25)',
              borderRadius: '0.875rem', fontSize: '0.875rem',
              color: '#c084fc', fontWeight: '500'
            }}>
              {t('goalAchievedNote')}
            </div>
          )}
          <button
            className="checkin-submit-btn"
            onClick={() => { setSubmitted(false); setCheckInType(null); setActiveTab('history'); }}
            style={{
              padding: '0.875rem 2rem',
              background: 'linear-gradient(135deg, #008081, #005f5f)',
              color: '#FFFFFF', borderRadius: '0.875rem',
              fontWeight: '700', border: 'none', cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(0,128,129,0.35)', fontSize: '0.95rem'
            }}
          >
            {t('viewHistory')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '42rem', margin: '0 auto' }}>
      <style>{styleTag}</style>
      <div style={card}>

        {/* Tab bar */}
        <div style={{
          display: 'flex', gap: '0.25rem', marginBottom: '2rem',
          borderBottom: `1px solid ${colors.tabBorder}`,
          overflowX: 'auto'
        }}>
          {[
            { id: 'new',     label: t('newCheckin') },
            { id: 'history', label: t('myHistory') },
            { id: 'partner', label: t('partnersProgress').replace('{name}', partner?.name || t('yourPartner')) },
          ].map(tab => (
            <button
              key={tab.id}
              className="checkin-tab-btn"
              onClick={() => { setActiveTab(tab.id); setCheckInType(null); }}
              style={{
                padding: '0.75rem 1rem',
                background: 'none', border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #008081' : '2px solid transparent',
                color: activeTab === tab.id ? '#008081' : colors.tabInactive,
                fontWeight: activeTab === tab.id ? '700' : '400',
                cursor: 'pointer', fontSize: '0.875rem',
                marginBottom: '-1px', whiteSpace: 'nowrap',
                transition: 'color 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* NEW CHECK-IN TAB */}
        {activeTab === 'new' && !checkInType && (
          <div style={{ animation: 'fadeInUp 0.3s ease' }}>
            {isComplete && (
              <div style={{
                marginBottom: '1.5rem', padding: '1rem',
                backgroundColor: 'rgba(22,163,74,0.1)',
                border: '1px solid rgba(22,163,74,0.25)',
                borderRadius: '0.875rem', fontSize: '0.875rem',
                color: '#4ade80', fontWeight: '500'
              }}>
                ✓ {t('veylComplete')}
              </div>
            )}
            {alreadyAchieved && !isComplete && (
              <div style={{
                marginBottom: '1.5rem', padding: '1rem',
                backgroundColor: 'rgba(192,132,252,0.1)',
                border: '1px solid rgba(192,132,252,0.25)',
                borderRadius: '0.875rem', fontSize: '0.875rem',
                color: '#c084fc', fontWeight: '500'
              }}>
                {t('goalAlreadyAchieved')}
              </div>
            )}
            {!isComplete && (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {[
                  { type: 'daily',  emoji: '📅', title: t('dailyCheckin'),  desc: t('dailyCheckinDesc'),  color: '#008081', shadow: 'rgba(0,128,129,0.2)' },
                  { type: 'weekly', emoji: '📆', title: t('weeklyCheckin'), desc: t('weeklyCheckinDesc'), color: '#c084fc', shadow: 'rgba(192,132,252,0.2)' },
                ].map(item => (
                  <button
                    key={item.type}
                    className="checkin-type-btn"
                    onClick={() => setCheckInType(item.type)}
                    style={{
                      padding: '1.5rem',
                      border: `1px solid ${colors.optionBorder}`,
                      borderRadius: '1rem', textAlign: 'left', cursor: 'pointer',
                      backgroundColor: colors.optionBg,
                      display: 'flex', alignItems: 'center', gap: '1rem'
                    }}
                  >
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '0.75rem',
                      background: `linear-gradient(135deg, ${item.color}22, ${item.color}11)`,
                      border: `1px solid ${item.color}33`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1.5rem', flexShrink: 0
                    }}>
                      {item.emoji}
                    </div>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '1rem', marginBottom: '0.3rem', color: colors.text }}>
                        {item.title}
                      </div>
                      <div style={{ color: colors.subtext, fontSize: '0.85rem', lineHeight: 1.5 }}>
                        {item.desc}
                      </div>
                    </div>
                    <div style={{ marginLeft: 'auto', color: colors.arrowColor, fontSize: '1.2rem', flexShrink: 0 }}>→</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* DAILY FORM */}
        {activeTab === 'new' && checkInType === 'daily' && (
          <div style={{ animation: 'fadeInUp 0.3s ease' }}>
            <button
              onClick={() => setCheckInType(null)}
              style={{ marginBottom: '1.25rem', color: colors.backBtn, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
            >
              ← {t('back')}
            </button>
            <h2 style={{ fontSize: '1.4rem', fontWeight: '800', marginBottom: '0.4rem', color: colors.text, letterSpacing: '-0.02em' }}>
              {t('dailyCheckin')}
            </h2>
            <p style={{ color: colors.subtext, marginBottom: '2rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
              {t('dailyCheckinDesc')}
            </p>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '700', marginBottom: '0.875rem', color: colors.labelColor, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {t('didYouWork')}
              </label>
              <div style={{ display: 'grid', gap: '0.6rem' }}>
                {[
                  { value: 'yes',  label: t('yesLabel'),  desc: t('madeProgress'),  color: '#4ade80' },
                  { value: 'some', label: t('aLittle'),   desc: t('didSomething'),  color: '#facc15' },
                  { value: 'no',   label: t('noLabel'),   desc: t('didntWork'),     color: '#f87171' },
                ].map(option => (
                  <button
                    key={option.value}
                    className="checkin-option-btn"
                    onClick={() => setDailyStatus(option.value)}
                    style={optionBtn(dailyStatus === option.value)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        backgroundColor: dailyStatus === option.value ? option.color : colors.optionDotOff,
                        flexShrink: 0, transition: 'background-color 0.15s'
                      }} />
                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '0.15rem', color: dailyStatus === option.value ? colors.text : colors.optionText, fontSize: '0.9rem' }}>
                          {option.label}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: colors.optionDesc }}>{option.desc}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {dailyStatus === 'no' && (
              <div style={{ marginBottom: '1.5rem', animation: 'fadeInUp 0.2s ease' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '700', marginBottom: '0.5rem', color: colors.labelColor, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {t('whatGotInWay')}
                </label>
                <textarea
                  className="checkin-textarea"
                  value={dailyNote}
                  onChange={(e) => setDailyNote(e.target.value)}
                  style={textarea}
                  placeholder={t('honestNote')}
                />
              </div>
            )}

            <GoalAchievedQuestion />

            <button
              className="checkin-submit-btn"
              onClick={submitDailyCheckIn}
              disabled={!dailyStatus || loading}
              style={{
                width: '100%', padding: '0.875rem',
                background: 'linear-gradient(135deg, #008081, #005f5f)',
                color: '#FFFFFF', borderRadius: '0.875rem',
                fontWeight: '700', border: 'none',
                cursor: (!dailyStatus || loading) ? 'not-allowed' : 'pointer',
                opacity: (!dailyStatus || loading) ? 0.4 : 1,
                boxShadow: '0 4px 15px rgba(0,128,129,0.3)', fontSize: '0.95rem'
              }}
            >
              {loading ? t('submitting') : t('submitDaily')}
            </button>
          </div>
        )}

        {/* WEEKLY FORM */}
        {activeTab === 'new' && checkInType === 'weekly' && (
          <div style={{ animation: 'fadeInUp 0.3s ease' }}>
            <button
              onClick={() => setCheckInType(null)}
              style={{ marginBottom: '1.25rem', color: colors.backBtn, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
            >
              ← {t('back')}
            </button>
            <h2 style={{ fontSize: '1.4rem', fontWeight: '800', marginBottom: '0.4rem', color: colors.text, letterSpacing: '-0.02em' }}>
              {t('weeklyCheckin')}
            </h2>
            <p style={{ color: colors.subtext, marginBottom: '2rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
              {t('weeklyCheckinDesc')}
            </p>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '700', marginBottom: '0.875rem', color: colors.labelColor, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {t('howDidWeekGo')}
              </label>
              <div style={{ display: 'grid', gap: '0.6rem' }}>
                {[
                  { value: 'did',        label: t('didIt'),          desc: t('metWeekly'),    color: '#4ade80' },
                  { value: 'partial',    label: t('didSome'),        desc: t('madePartial'),  color: '#facc15' },
                  { value: 'didnt',      label: t('didntDoIt'),      desc: t('didntMake'),    color: '#f87171' },
                  { value: 'rethinking', label: t('rethinkingGoal'), desc: t('needToAdjust'), color: '#c084fc' },
                ].map(option => (
                  <button
                    key={option.value}
                    className="checkin-option-btn"
                    onClick={() => setWeeklyStatus(option.value)}
                    style={optionBtn(weeklyStatus === option.value)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        backgroundColor: weeklyStatus === option.value ? option.color : colors.optionDotOff,
                        flexShrink: 0, transition: 'background-color 0.15s'
                      }} />
                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '0.15rem', color: weeklyStatus === option.value ? colors.text : colors.optionText, fontSize: '0.9rem' }}>
                          {option.label}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: colors.optionDesc }}>{option.desc}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {[
              { labelKey: 'whatDidYouLearn', value: weeklyLearning, setter: setWeeklyLearning, placeholderKey: 'insightsPlaceholder' },
              { labelKey: 'whatPatterns',    value: weeklyPattern,  setter: setWeeklyPattern,  placeholderKey: 'patternsPlaceholder' },
              { labelKey: 'whatChange',      value: weeklyChange,   setter: setWeeklyChange,   placeholderKey: 'changePlaceholder' },
            ].map(({ labelKey, value, setter, placeholderKey }) => (
              <div key={labelKey} style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '700', marginBottom: '0.5rem', color: colors.labelColor, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {t(labelKey)}
                </label>
                <textarea
                  className="checkin-textarea"
                  value={value}
                  onChange={(e) => setter(e.target.value)}
                  style={textarea}
                  placeholder={t(placeholderKey)}
                />
              </div>
            ))}

            <GoalAchievedQuestion />

            <button
              className="checkin-submit-btn"
              onClick={submitWeeklyCheckIn}
              disabled={!weeklyStatus || !weeklyLearning || !weeklyPattern || !weeklyChange || loading}
              style={{
                width: '100%', padding: '0.875rem',
                background: 'linear-gradient(135deg, #008081, #005f5f)',
                color: '#FFFFFF', borderRadius: '0.875rem',
                fontWeight: '700', border: 'none',
                cursor: (!weeklyStatus || !weeklyLearning || !weeklyPattern || !weeklyChange || loading) ? 'not-allowed' : 'pointer',
                opacity: (!weeklyStatus || !weeklyLearning || !weeklyPattern || !weeklyChange || loading) ? 0.4 : 1,
                boxShadow: '0 4px 15px rgba(0,128,129,0.3)', fontSize: '0.95rem'
              }}
            >
              {loading ? t('submitting') : t('submitWeekly')}
            </button>
          </div>
        )}

        {/* MY HISTORY TAB */}
        {activeTab === 'history' && (
          <div style={{ animation: 'fadeInUp 0.3s ease' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '800', marginBottom: '0.25rem', color: colors.text }}>
                {t('myCheckinHistory')}
              </h3>
              <p style={{ fontSize: '0.8rem', color: colors.mutedText, margin: 0 }}>
                {t('checkinsForVeyl', { count: myCheckIns.length, plural: myCheckIns.length !== 1 ? 's' : '' })}
              </p>
            </div>

            {myCheckIns.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 0', color: colors.emptyText }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📋</div>
                <p style={{ fontSize: '0.875rem', margin: '0 0 1.5rem' }}>{t('noCheckinsYet')}</p>
                <button
                  className="checkin-submit-btn"
                  onClick={() => setActiveTab('new')}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, #008081, #005f5f)',
                    color: '#FFFFFF', borderRadius: '0.875rem',
                    fontWeight: '700', border: 'none', cursor: 'pointer',
                    fontSize: '0.875rem', boxShadow: '0 4px 15px rgba(0,128,129,0.3)'
                  }}
                >
                  {t('newCheckinBtn')}
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {myCheckIns.map(item => (
                  <div
                    key={item.id}
                    className="checkin-history-card"
                    style={{
                      padding: '1.25rem',
                      border: `1px solid ${colors.historyCardBorder}`,
                      borderRadius: '1rem',
                      backgroundColor: colors.historyCardBg
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <span style={{ fontWeight: '700', color: colors.text, fontSize: '0.875rem' }}>
                        {item.type === 'daily' ? '📅 ' + t('dailyCheckin') : '📆 ' + t('weeklyCheckin')}
                      </span>
                      <span style={{ fontSize: '0.72rem', color: colors.dateBadgeText, backgroundColor: colors.dateBadgeBg, padding: '0.2rem 0.6rem', borderRadius: '9999px' }}>
                        {item.timestamp?.toDate().toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: colors.statusText, marginBottom: '0.5rem' }}>
                      {statusLabel(item.status)}
                    </div>
                    {item.type === 'weekly' && (
                      <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.4rem' }}>
                        {item.learning && (
                          <div style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem', backgroundColor: colors.myInsightBg, border: `1px solid ${colors.myInsightBorder}`, borderRadius: '0.5rem', color: colors.myInsightText }}>
                            <span style={{ fontWeight: '700', color: '#008081' }}>{t('learnedLabel')}</span>{item.learning}
                          </div>
                        )}
                        {item.pattern && (
                          <div style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem', backgroundColor: colors.myInsightBg, border: `1px solid ${colors.myInsightBorder}`, borderRadius: '0.5rem', color: colors.myInsightText }}>
                            <span style={{ fontWeight: '700', color: '#008081' }}>{t('patternLabel')}</span>{item.pattern}
                          </div>
                        )}
                        {item.change && (
                          <div style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem', backgroundColor: colors.myInsightBg, border: `1px solid ${colors.myInsightBorder}`, borderRadius: '0.5rem', color: colors.myInsightText }}>
                            <span style={{ fontWeight: '700', color: '#008081' }}>{t('nextWeekLabel')}</span>{item.change}
                          </div>
                        )}
                      </div>
                    )}
                    {item.goalAchieved && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#c084fc', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        🎉 {t('goalAchievedMarked')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PARTNER'S PROGRESS TAB */}
        {activeTab === 'partner' && (
          <div style={{ animation: 'fadeInUp 0.3s ease' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '800', marginBottom: '0.25rem', color: colors.text }}>
                {t('progressOf').replace('{name}', partner?.name || t('yourPartner'))}
              </h3>
              <p style={{ fontSize: '0.8rem', color: colors.mutedText, margin: 0 }}>
                {t('checkinsForVeyl', { count: partnerCheckIns.length, plural: partnerCheckIns.length !== 1 ? 's' : '' })} · {t('dailyNotesPrivate')}
              </p>
            </div>

            {!partnerId ? (
              <div style={{ textAlign: 'center', padding: '3rem 0', color: colors.emptyText }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>👤</div>
                <p style={{ fontSize: '0.875rem', margin: 0 }}>{t('loadingPartner')}</p>
              </div>
            ) : partnerCheckIns.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 0', color: colors.emptyText }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⏳</div>
                <p style={{ fontSize: '0.875rem', margin: 0 }}>{t('partnerNoCheckins').replace('{name}', partner?.name || t('yourPartner'))}</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {partnerCheckIns.map(item => (
                  <div
                    key={item.id}
                    className="checkin-history-card"
                    style={{
                      padding: '1.25rem',
                      border: `1px solid ${colors.historyCardBorder}`,
                      borderRadius: '1rem',
                      backgroundColor: colors.historyCardBg
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <span style={{ fontWeight: '700', color: colors.text, fontSize: '0.875rem' }}>
                        {item.type === 'daily' ? '📅 ' + t('dailyCheckin') : '📆 ' + t('weeklyCheckin')}
                      </span>
                      <span style={{ fontSize: '0.72rem', color: colors.dateBadgeText, backgroundColor: colors.dateBadgeBg, padding: '0.2rem 0.6rem', borderRadius: '9999px' }}>
                        {item.timestamp?.toDate().toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: colors.statusText, marginBottom: '0.5rem' }}>
                      {statusLabel(item.status)}
                    </div>
                    {item.type === 'weekly' && (
                      <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.4rem' }}>
                        {item.learning && (
                          <div style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem', backgroundColor: colors.pInsightBg, border: `1px solid ${colors.pInsightBorder}`, borderRadius: '0.5rem', color: colors.pInsightText }}>
                            <span style={{ fontWeight: '700', color: '#c084fc' }}>{t('learnedLabel')}</span>{item.learning}
                          </div>
                        )}
                        {item.pattern && (
                          <div style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem', backgroundColor: colors.pInsightBg, border: `1px solid ${colors.pInsightBorder}`, borderRadius: '0.5rem', color: colors.pInsightText }}>
                            <span style={{ fontWeight: '700', color: '#c084fc' }}>{t('patternLabel')}</span>{item.pattern}
                          </div>
                        )}
                        {item.change && (
                          <div style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem', backgroundColor: colors.pInsightBg, border: `1px solid ${colors.pInsightBorder}`, borderRadius: '0.5rem', color: colors.pInsightText }}>
                            <span style={{ fontWeight: '700', color: '#c084fc' }}>{t('nextWeekLabel')}</span>{item.change}
                          </div>
                        )}
                      </div>
                    )}
                    {item.type === 'daily' && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: colors.lockText, fontStyle: 'italic' }}>
                        🔒 {t('dailyNotesPrivate')}
                      </div>
                    )}
                    {item.goalAchieved && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#c084fc', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        🎉 {t('goalAchievedMarked')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}